import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import type { ReactNode } from "react";
import type {
  TenantConfig,
  TenantContextValue,
  UnitSystem,
  TenantUnitLabels,
  TenantConversionFactors,
} from "@config/tenants/types";
import {
  getTenantConfig,
  getUnitLabelsForSystem,
  getConversionFactorsForSystem,
  convertVolume as convertVolumeUtil,
  convertTemperature as convertTemperatureUtil,
  formatVolume as formatVolumeUtil,
} from "@config/tenants";

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
  /** Override tenant ID (defaults to VITE_TENANT_ID env var) */
  tenantId?: string;
}

/**
 * Local storage key for user's unit system preference
 */
const UNIT_SYSTEM_STORAGE_KEY = "wint-unit-system";

/**
 * Get initial tenant ID from environment or URL
 */
function getInitialTenantId(override?: string): string {
  if (override) return override;

  // Check environment variable
  const envTenantId = import.meta.env.VITE_TENANT_ID;
  if (envTenantId) return envTenantId;

  // Default to pepsico
  return "pepsico";
}

/**
 * Get initial unit system from localStorage or tenant defaults
 */
function getInitialUnitSystem(config: TenantConfig): UnitSystem {
  const stored = localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY);
  if (stored === "metric" || stored === "imperial") {
    return stored;
  }
  return config.defaults.unitSystem;
}

export function TenantProvider({
  children,
  tenantId: tenantIdOverride,
}: TenantProviderProps) {
  const tenantId = getInitialTenantId(tenantIdOverride);
  const config = useMemo(() => getTenantConfig(tenantId), [tenantId]);

  const [unitSystem, setUnitSystemState] = useState<UnitSystem>(() =>
    getInitialUnitSystem(config)
  );

  // Apply tenant data attribute to document for CSS theming
  useEffect(() => {
    document.documentElement.setAttribute("data-tenant", config.id);
    document.title = `Wint - ${config.branding.companyName} Dashboard`;

    // Update favicon if specified
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon && config.branding.favicon) {
      favicon.href = config.branding.favicon;
    }
  }, [config]);

  // Persist unit system to localStorage
  const setUnitSystem = useCallback((system: UnitSystem) => {
    setUnitSystemState(system);
    localStorage.setItem(UNIT_SYSTEM_STORAGE_KEY, system);
  }, []);

  const getUnitLabels = useCallback((): TenantUnitLabels => {
    return getUnitLabelsForSystem(unitSystem);
  }, [unitSystem]);

  const getConversionFactors = useCallback((): TenantConversionFactors => {
    return getConversionFactorsForSystem(unitSystem);
  }, [unitSystem]);

  const convertVolume = useCallback(
    (liters: number): number => {
      return convertVolumeUtil(liters, unitSystem, true);
    },
    [unitSystem]
  );

  const convertTemperature = useCallback(
    (celsius: number): number => {
      return convertTemperatureUtil(celsius, unitSystem);
    },
    [unitSystem]
  );

  const formatVolume = useCallback(
    (liters: number): string => {
      return formatVolumeUtil(liters, unitSystem, true);
    },
    [unitSystem]
  );

  const contextValue = useMemo<TenantContextValue>(
    () => ({
      config,
      tenantId,
      unitSystem,
      setUnitSystem,
      getUnitLabels,
      getConversionFactors,
      convertVolume,
      convertTemperature,
      formatVolume,
    }),
    [
      config,
      tenantId,
      unitSystem,
      setUnitSystem,
      getUnitLabels,
      getConversionFactors,
      convertVolume,
      convertTemperature,
      formatVolume,
    ]
  );

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 * @throws Error if used outside TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

/**
 * Hook for accessing tenant terminology
 */
export function useTerminology() {
  const { config } = useTenant();
  return config.terminology;
}

/**
 * Hook for accessing tenant features
 */
export function useFeatures() {
  const { config } = useTenant();
  return config.features;
}

/**
 * Hook for accessing tenant branding
 */
export function useBranding() {
  const { config } = useTenant();
  return config.branding;
}

/**
 * Hook for accessing tenant routes
 */
export function useRoutes() {
  const { config } = useTenant();
  return config.routes;
}

/**
 * Hook for unit conversion utilities
 * Returns a stable object reference to prevent infinite re-renders
 */
export function useUnitConversion() {
  const {
    unitSystem,
    setUnitSystem,
    getUnitLabels,
    getConversionFactors,
    convertVolume,
    convertTemperature,
    formatVolume,
  } = useTenant();

  // Memoize the return value to prevent new object reference on each render
  return useMemo(
    () => ({
      unitSystem,
      setUnitSystem,
      getUnitLabels,
      getConversionFactors,
      convertVolume,
      convertTemperature,
      formatVolume,
      isMetric: unitSystem === "metric",
      isImperial: unitSystem === "imperial",
    }),
    [
      unitSystem,
      setUnitSystem,
      getUnitLabels,
      getConversionFactors,
      convertVolume,
      convertTemperature,
      formatVolume,
    ]
  );
}

export default TenantProvider;
